import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  Loader2,
  Download,
  Clock,
  AlertTriangle,
  FolderGit2,
  FileDiff,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { CampaignService, type Campaign, type CampaignRepo } from '../services/campaignService';

function StatusIcon({ status, stalled }: { status: CampaignRepo['status']; stalled: boolean }) {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  if (status === 'failed') return <XCircle className="w-4 h-4 text-red-500" />;
  if (status === 'skipped') return <MinusCircle className="w-4 h-4 text-gray-400" />;
  // A spinner implies work is happening. When the workers haven't
  // picked the campaign up, show a truthful waiting state instead.
  if (stalled) return <Clock className="w-4 h-4 text-gray-400" />;
  return <Loader2 className="w-4 h-4 text-teal-500 animate-spin" />;
}

function downloadPatch(repo: CampaignRepo) {
  if (!repo.patch) return;
  const blob = new Blob([repo.patch], { type: 'text/x-patch' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${repo.repoName}.patch`;
  a.click();
  URL.revokeObjectURL(url);
}

function CampaignSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="mt-8 mb-6 h-48 rounded-3xl bg-gray-100 dark:bg-gray-800/60" />
      <div className="h-72 rounded-2xl bg-gray-100 dark:bg-gray-800/60" />
    </div>
  );
}

export const CampaignPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await CampaignService.get(id);
      setCampaign(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while the campaign is still running so the grid fills in live.
  useEffect(() => {
    if (!campaign || campaign.status === 'completed') return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [campaign, load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Campaign" showDivider={true} />
        <main className="pt-(--navbar-offset) max-w-4xl mx-auto px-4 pb-20">
          <CampaignSkeleton />
        </main>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Campaign" showDivider={true} />
        <div className="text-center pt-32 text-red-500">{error || 'Not found'}</div>
      </div>
    );
  }

  const totalFiles = campaign.repos.reduce((a, r) => a + r.filesChanged, 0);
  const patched = campaign.repos.filter((r) => r.patch);

  // No repo has progressed well past creation: the Temporal workers
  // haven't picked the workflow up. Say so instead of spinning.
  const stalled =
    campaign.status !== 'completed' &&
    campaign.repos.every((r) => r.status === 'pending') &&
    Date.now() - new Date(campaign.createdAt).getTime() > 60_000;

  const downloadAll = () => patched.forEach(downloadPatch);

  return (
    <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
      <Navbar pageTitle="Fleet campaign" showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-4xl mx-auto px-4 pb-20">
        {/* Hero header */}
        <div className="relative mt-8 mb-4 overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-teal-50 via-white to-white dark:from-teal-950/30 dark:via-gray-900/40 dark:to-gray-900/40 p-6 sm:p-8">
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {campaign.issueTitle}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                <code className="text-xs rounded-md bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-gray-700 dark:text-gray-300">
                  {campaign.taskType}
                </code>{' '}
                · all delivered as local patches (no upstream writes)
              </p>
            </div>
            {patched.length > 0 && (
              <button
                onClick={downloadAll}
                className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow"
              >
                <Download className="w-4 h-4" /> Download all patches
              </button>
            )}
          </div>

          {/* Stat tiles */}
          <div className="relative mt-6 grid grid-cols-2 gap-3 sm:max-w-sm">
            <div className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 p-4">
              <FolderGit2 className="w-4 h-4 mb-2 text-teal-500" />
              <div className="text-3xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                {campaign.okRepos}
                <span className="text-lg font-semibold text-gray-400 dark:text-gray-500">
                  /{campaign.totalRepos}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">repos patched</div>
            </div>
            <div className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 p-4">
              <FileDiff className="w-4 h-4 mb-2 text-teal-500" />
              <div className="text-3xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                {totalFiles.toLocaleString()}
              </div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">files changed</div>
            </div>
          </div>
        </div>

        {stalled && (
          <div className="mb-4 flex items-start gap-3 p-4 rounded-2xl border border-amber-200/70 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 shadow-sm">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </span>
            <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
              Waiting for execution workers — no repository has been picked up yet. The campaign is
              queued in Temporal and will proceed when the workers are running the latest build;
              nothing is lost by waiting.
            </p>
          </div>
        )}

        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">
                  Repository
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">Branch</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">Files</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {campaign.repos.map((repo) => (
                <tr
                  key={repo.id}
                  className="border-t border-gray-100 dark:border-gray-800 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30"
                >
                  <td className="px-4 py-2.5">
                    <StatusIcon status={repo.status} stalled={stalled} />
                  </td>
                  <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-200">
                    {repo.repoName}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                    {repo.branch || (repo.message ? repo.message.slice(0, 40) : '—')}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 tabular-nums">
                    {repo.filesChanged || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {repo.patch && (
                      <button
                        onClick={() => downloadPatch(repo)}
                        className="inline-flex items-center gap-1 rounded-lg border border-teal-200 dark:border-teal-800 px-2 py-1 text-xs text-teal-700 dark:text-teal-400 transition-colors hover:bg-teal-50 dark:hover:bg-teal-900/30 active:scale-[.97]"
                      >
                        <Download className="w-3 h-3" /> patch
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <Footer />
    </div>
  );
};
